import { ChartFiltersService } from './chart-filters.service';
import { Injectable } from '@angular/core';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import { idMapToArray, ScopeService } from './scope.service';
import { YearsService } from './years.service';
import { HARD_CODED_TYPE_OF_USE, TypeOfUse } from './emission-factor.service';
import { PerimeterPathwayService } from './perimeter-pathway.service';
import { PerimetersService } from './perimeters.service';

export interface ObservableMap<T> {
  [key: string]: Observable<T>;
}

@Injectable({
  providedIn: 'root',
})
export class ChartsService {

  private defaultStartYear: number;
  private defaultEndYear: number;


  constructor(
    private api: APIService,
    private scopeService: ScopeService,
    private yearsService: YearsService,
    private perimeterPathwayService: PerimeterPathwayService,
    private perimetersService: PerimetersService,
    private chartFiltersService: ChartFiltersService,
  ) { }

  public _energyHasChanged$ = new BehaviorSubject<void>(undefined);
  public energyHasChanged() {
    this._energyHasChanged$.next();
  }

  public _carbonEmissionsHasChanged$ = new BehaviorSubject<void>(undefined);
  public carbonEmissionsHasChanged() {
    this._carbonEmissionsHasChanged$.next();
  }

  public _capexHasChanged$ = new BehaviorSubject<void>(undefined);
  public capexHasChanged() {
    this._capexHasChanged$.next();
  }

  public _declaredConsumptionsHasChanged$ = new BehaviorSubject<void>(undefined);
  public declaredConsumptionsHasChanged() {
    this._declaredConsumptionsHasChanged$.next();
  }

  private assetsByState: ObservableMap<KPI.AssetsByYearAndNote> = {};
  public getAssetsByState(options): Observable<KPI.AssetsByYearAndNote> {
    const key = this.getKey(arguments);
    if (!this.assetsByState[key]) {
      this.assetsByState[key] = this._capexHasChanged$.pipe(
        switchMap(() => this.api.get_assets_by_state(options)),
        shareReplay(1)
      );
    }
    return this.assetsByState[key];
  }

  private assetsRenewals: ObservableMap<KPI.AssetsRenewalsChartData> = {};
  public getAssetsRenewals(options): Observable<KPI.AssetsRenewalsChartData> {
    const key = this.getKey(arguments);
    if (!this.assetsRenewals[key]) {
      this.assetsRenewals[key] = this.api.get_assets_renewals(options).pipe(shareReplay(1));
    }
    return this.assetsRenewals[key];
  }

  private assetsTotalByFilter: ObservableMap<any> = {};
  public getAssetsTotalByFilter(options): Observable<any> {
    const key = this.getKey(arguments);
    if (!this.assetsTotalByFilter[key]) {
      this.assetsTotalByFilter[key] = this.api.get_assets_total_by_filter(options).pipe(shareReplay(1));
    }
    return this.assetsTotalByFilter[key];
  }

  public getInvestmentsByPriority(options): Observable<any> {
    return this.api.get_investments_by_priority(options);
  }

  private ppaByCluster: ObservableMap<KPI.PPAByCluster> = {};
  public getPPAByCluster(options): Observable<KPI.PPAByCluster> {
    const key = this.getKey(arguments);
    if (!this.ppaByCluster[key]) {
      this.ppaByCluster[key] = this.api.get_ppa_by_cluster(options).pipe(shareReplay(1));
    }
    return this.ppaByCluster[key];
  }

  private initiativeCashFlow: ObservableMap<KPI.InitiativeCashFlow> = {};
  public getInitiativeCashFlow(initiativeId, queryParams): Observable<KPI.InitiativeCashFlow> {
    const key = this.getKey(arguments);
    if (!this.initiativeCashFlow[key]) {
      this.initiativeCashFlow[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.get_initiative_cash_flow(initiativeId, queryParams)),
        shareReplay(1)
      );
    }
    return this.initiativeCashFlow[key];
  }

  private kpiExpertChart: ObservableMap<KPI.KPIExpertChart> = {};
  public getKPIExpertChart(options): Observable<KPI.KPIExpertChart> {
    const key = this.getKey(arguments);
    if (!this.kpiExpertChart[key]) {
      this.kpiExpertChart[key] = this.api.get_kpi_expert_chart(options).pipe(
        shareReplay(1),
      );
    }
    return this.kpiExpertChart[key];
  }

  private ghgEmissionsChart: ObservableMap<KPI.GHGEmissionsChart> = {};
  public getGHGEmissionsChart(options): Observable<KPI.GHGEmissionsChart> {
    const key = this.getKey(arguments);
    if (!this.ghgEmissionsChart[key]) {
      this.ghgEmissionsChart[key] = combineLatest([
        this._energyHasChanged$,
        this._carbonEmissionsHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.get_ghg_absolute_emission_avoided(options)),
        shareReplay(1)
      );
    }
    return this.ghgEmissionsChart[key];
  }

  private energyTrajectoryData: ObservableMap<KPI.EnergyTrajectory> = {};
  public getEnergyTrajectoryData(options): Observable<KPI.EnergyTrajectory> {
    const key = this.getKey(arguments);
    if (!this.energyTrajectoryData[key]) {
      this.energyTrajectoryData[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
        this.yearsService.latestYears$,
        this.chartFiltersService.filter$('energyConsumptionType', false),
      ]).pipe(
        switchMap(([_energyHasChanged, _capexHasChanged, [startYear, endYear], consumption_key]) => {
          this.defaultStartYear = startYear;
          this.defaultEndYear = endYear;
          return this.api.get_energy_trajectory({
            ...options,
            consumption_key
          });
        }),
        map((trajectory) => {
          const startYear = options.startYear ? options.startYear : this.defaultStartYear
          const endYear = options.endYear ? options.endYear : this.defaultEndYear
          return {
            ...trajectory,
            trajectory_points: R.range(startYear, endYear + 1).map((year) => {
              const point = trajectory.trajectory_points.find((elm) => elm.year == year)
              if (point) {
                return point;
              } else {
                return {
                  year,
                  area: null,
                  energy_consumption: null,
                  objective_area: null,
                  objective: null,
                  is_objective_declared: undefined,
                  declared_objective_area: null,
                  declared_objective: null,
                  sites_count: null,
                  economy_marginal_in_consumption: null,
                  is_extrapolated: undefined,
                  investments_by_year_and_status: [],
                  contributions: null
                }
              }
            })
          }
        }),
        shareReplay(1),
      );
    }

    return this.energyTrajectoryData[key];
  }
  private ghgIntensityPathway: ObservableMap<KPI.GHGIntensityPathway[]> = {};
  public getCarbonIntensityPathway(options): Observable<KPI.GHGIntensityPathway[]> {
    const key = this.getKey(arguments);
    if (!this.ghgIntensityPathway[key]) {
      if (!options.provider) {
        this.ghgIntensityPathway[key] = combineLatest([
          this.perimetersService.perimeters$,
          this.yearsService.latestYears$,
        ]).pipe(
          switchMap(([perimeters, [startYear, endYear]]) => this.perimeterPathwayService.getGlobalPathway$(
            perimeters,
            [startYear, endYear],
          )),
          map((pathway) => {
            const res: KPI.GHGIntensityPathway[] = Object.keys(pathway).map((year) => ({
              year: Number(year),
              ghg_intensity: pathway[year].ghg_objective / pathway[year].area,
              type_of_use: options.type_of_use,
              provider: options.provider,
              country: 'FR',
            }));
            return res;
          }),
        );
      } else {
        this.ghgIntensityPathway[key] = this.api.get_ghg_intensity_pathway(options).pipe(
          shareReplay(1),
        );
      }
    }
    return this.ghgIntensityPathway[key];
  }

  private energyIntensityPathway: ObservableMap<KPI.EnergyIntensityPathway[]> = {};
  public getEnergyIntensityPathway(
    options: { type_of_use: TypeOfUse, provider: number },
    forcedPerimeters?: KPI.Perimeter[],
  ): Observable<KPI.EnergyIntensityPathway[]> {
    const key = this.getKey(arguments);
    if (!this.energyIntensityPathway[key]) {
      if (!options.provider) {
        this.energyIntensityPathway[key] = combineLatest([
          this.perimetersService.perimeters$,
          this.yearsService.latestYears$,
        ]).pipe(
          switchMap(([perimeters, [startYear, endYear]]) => this.perimeterPathwayService.getGlobalPathway$(
            forcedPerimeters?.length ? forcedPerimeters : perimeters,
            [startYear, endYear],
          )),
          map((pathway) => {
            const res: KPI.EnergyIntensityPathway[] = Object.keys(pathway).map((year) => ({
              year: Number(year),
              energy_intensity: pathway[year].kwh_objective / pathway[year].area,
              type_of_use: options.type_of_use,
              provider: options.provider,
              country: 'FR',
            }));
            return res;
          }),
        );
      } else {
        this.energyIntensityPathway[key] = this.api.get_energy_intensity_pathway(options).pipe(
          shareReplay(1),
        );
      }
    }
    return this.energyIntensityPathway[key];
  }

  private scenarioInitiativeComparison: ObservableMap<KPI.ScenarioInitiativeComparison> = {};
  public getScenarioInitiativeComparison(options): Observable<KPI.ScenarioInitiativeComparison> {
    const key = this.getKey(arguments);
    if (!this.scenarioInitiativeComparison[key]) {
      this.scenarioInitiativeComparison[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.get_scenario_initiative_comparison(options)),
        shareReplay(1)
      );
    }
    return this.scenarioInitiativeComparison[key];
  }


  private investmentsByEuropeanTaxonomyEligibility: ObservableMap<KPI.TotalInvestmentsByTaxonomyEligibility> = {};
  public getInvestmentsByEuropeanTaxonomyEligibility(options): Observable<KPI.TotalInvestmentsByTaxonomyEligibility> {
    const key = this.getKey(arguments);
    if (!this.investmentsByEuropeanTaxonomyEligibility[key]) {
      this.investmentsByEuropeanTaxonomyEligibility[key] = this.api.get_european_taxonomy_eligibility(options).pipe(
        shareReplay(1),
      );
    }
    return this.investmentsByEuropeanTaxonomyEligibility[key];
  }

  private eligibleByTaxonomyCat: ObservableMap<KPI.EligibilityByTaxonomyCat> = {};
  public getEligibleByTaxonomyCat(options): Observable<KPI.EligibilityByTaxonomyCat> {
    const key = this.getKey(arguments);
    if (!this.eligibleByTaxonomyCat[key]) {
      this.eligibleByTaxonomyCat[key] = this.api.get_eligible_by_taxonomy_category(options).pipe(
        shareReplay(1),
      );
    }
    return this.eligibleByTaxonomyCat[key];
  }

  private strandedSitesTotal: ObservableMap<KPI.StrandedSites> = {};
  public getStrandedSites(options): Observable<KPI.StrandedSites> {
    const key = this.getKey(arguments);
    if (!this.strandedSitesTotal[key]) {
      this.strandedSitesTotal[key] = this.api.get_stranded_sites(options).pipe(
        shareReplay(1),
      );
    }
    return this.strandedSitesTotal[key];
  }

  private initiativeTrajectoryData: ObservableMap<KPI.InitiativeEnergyTrajectory> = {};
  public getInitiativeTrajectoryData(initiativeId: number, consumption_key: KPI.ConsumptionType): Observable<KPI.InitiativeEnergyTrajectory> {
    const key = this.getKey(arguments);
    if (!this.initiativeTrajectoryData[key]) {
      this.initiativeTrajectoryData[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.get_initiative_energy_trajectory(initiativeId, {consumption_key})),
        shareReplay(1),
      );
    }
    return this.initiativeTrajectoryData[key];
  }

  private initiativeTrajectoryDataForAsset: ObservableMap<KPI.InitiativeEnergyTrajectory> = {};
  public getInitiativesTrajectoryDataForAsset(assetId: number): Observable<KPI.InitiativeEnergyTrajectory> {
    const key = this.getKey(arguments);
    if (!this.initiativeTrajectoryDataForAsset[key]) {
      this.initiativeTrajectoryDataForAsset[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.get_initiative_energy_trajectory_for_asset(assetId)),
        shareReplay(1),
      );
    }
    return this.initiativeTrajectoryDataForAsset[key];
  }

  private initiativeTrajectoryDataForInvest: ObservableMap<KPI.InitiativeEnergyTrajectory> = {};
  public getInitiativesTrajectoryDataForInvest(investmentId: number): Observable<KPI.InitiativeEnergyTrajectory> {
    const key = this.getKey(arguments);
    if (!this.initiativeTrajectoryDataForInvest[key]) {
      this.initiativeTrajectoryDataForInvest[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.get_initiative_energy_trajectory_for_invest(investmentId)),
        shareReplay(1),
      );
    }
    return this.initiativeTrajectoryDataForInvest[key];
  }

  private initiativeTrajectoryDataForProject: ObservableMap<KPI.InitiativeEnergyTrajectory> = {};
  public getInitiativesTrajectoryDataForProject(projectId: number, consumption_key: KPI.ConsumptionType): Observable<KPI.InitiativeEnergyTrajectory> {
    const key = this.getKey(arguments);
    if (!this.initiativeTrajectoryDataForProject[key]) {
      this.initiativeTrajectoryDataForProject[key] = combineLatest([
        this.perimetersService.perimeters$.pipe(first()),
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(([perimeters]) => this.api.get_initiative_energy_trajectory_for_project(
          projectId,
          {
            buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
            consumption_key,
          }
        )),
        shareReplay(1),
      );
    }
    return this.initiativeTrajectoryDataForProject[key];
  }

  private carbonTrajectory: ObservableMap<KPI.CarbonTrajectory> = {};
  public getCarbonTrajectory(options): Observable<KPI.CarbonTrajectory> {
    const key = this.getKey(arguments);
    if (!this.carbonTrajectory[key]) {
      this.carbonTrajectory[key] = combineLatest([
        this.chartFiltersService.filter$('carbonConsumptionType', false),
        this._energyHasChanged$,
        this._capexHasChanged$,
        this._carbonEmissionsHasChanged$,
      ]).pipe(
        switchMap(([consumption_key]) => this.api.get_carbon_trajectory({
          ...options,
          consumption_key
        })),
        shareReplay(1),
      );
    }
    return this.carbonTrajectory[key];
  }

  private carbonTrajectoryData: ObservableMap<KPI.CarbonTrajectoryChartData> = {};
  public getCarbonTrajectoryData(options): Observable<KPI.CarbonTrajectoryChartData> {
    const key = this.getKey(arguments);

    if (!this.carbonTrajectoryData[key]) {
      this.carbonTrajectoryData[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => {
          return combineLatest([
            this.getCarbonTrajectory(options),
            this.getCarbonIntensityPathway({ type_of_use: HARD_CODED_TYPE_OF_USE, target: '1.5' }),
            this.getCarbonIntensityPathway({ type_of_use: HARD_CODED_TYPE_OF_USE, target: '2.0' }),
            this.getEnergyTrajectoryData({
              buildings: options.buildings.join(','),
              scenario: options.scenario,
            }),
            this.scopeService.investmentStatuses$.pipe(idMapToArray()),
          ]);
        }),
        map(([carbonTrajectory, ghgIntensityPathway1, ghgIntensityPathway2, energyTrajectory, investmentStatuses]) => {
          const plannedInvestmentStatuses = investmentStatuses.filter((status) =>
            !status.hypothesis && status.show_in_charts,
          );
          const chartData = {};
          const years = R.range(options['start_year'], options['end_year'] + 1);

          years.map((year) => {
            const point = energyTrajectory?.trajectory_points.find((traj) => traj.year === year)
              ? energyTrajectory.trajectory_points.find((traj) => traj.year === year)
              : {
                investments_by_year_and_status: [],
                area: 0
              };
            const invest = point.investments_by_year_and_status.filter((status) => plannedInvestmentStatuses.map((s) => s.id).includes(status.id))
            chartData[year] = {
              carbonTrajectory: carbonTrajectory.trajectory_points.find((traj) => traj.year === year),
              ghgIntensityPathway1: ghgIntensityPathway1.find((pathway) => pathway.year === year),
              ghgIntensityPathway2: ghgIntensityPathway2.find((pathway) => pathway.year === year),
              budget: point
                ? {
                  area: point.area,
                  investment_budget: invest.reduce((acc, s) => acc += s.investment_price, 0),
                  investment_number: invest.reduce((acc, s) => acc += s.number_of_investment, 0),
                }
                : null,
            };
          });
          return chartData;
        }),
        shareReplay(1),
      );
    }

    return this.carbonTrajectoryData[key];
  }

  private siteConsumption: ObservableMap<KPI.SiteConsumption> = {};
  public getSiteConsumption(multiPerimeterId: number): Observable<KPI.SiteConsumption> {
    const key = this.getKey(arguments);
    if (!this.siteConsumption[key]) {
      this.siteConsumption[key] = this._declaredConsumptionsHasChanged$.pipe(
        switchMap(() => this.api.get_site_consumptions(multiPerimeterId)),
        shareReplay(1),
      );
    }
    return this.siteConsumption[key];
  }

  private siteAssetConsumptions: ObservableMap<KPI.SiteAssetConsumptionsChartData> = {};
  public getSiteAssetConsumptions(multiPerimeterId: number, assetId: number): Observable<KPI.SiteAssetConsumptionsChartData> {
    const key = this.getKey(arguments);
    if (!this.siteAssetConsumptions[key]) {
      this.siteAssetConsumptions[key] = this.getSiteConsumption(multiPerimeterId).pipe(
        map((siteConsumption) => {
          const chartData: KPI.SiteAssetConsumptionsChartData = {};

          if (siteConsumption?.perimeters?.length) {
            siteConsumption.perimeters.map((p) => {
              if (p.trajectories?.length) {
                p.trajectories.map((t) => {
                  if (t.energy_type && t.perimeter_consumptions?.length) {
                    const lastTrajCons: KPI.PerimeterTrajectoryConsumption = t.perimeter_consumptions.sort((a, b) =>
                      a.year - b.year
                    )[t.perimeter_consumptions.length - 1];
                    if (lastTrajCons) {
                      if (!chartData[t.energy_type?.id]) {
                        chartData[t.energy_type?.id] = {
                          energy_color: t.energy_type.color,
                          energy_name: t.energy_type.name,
                          site_consumption: lastTrajCons.consumption,
                          unit: t.energy_type.unit,
                          asset_consumption: 0,
                        };
                      } else {
                        chartData[t.energy_type?.id].site_consumption += lastTrajCons.consumption;
                      }

                      if (lastTrajCons.asset_consumptions?.length) {
                        const assetConsumption: KPI.AssetConsumption = lastTrajCons.asset_consumptions.find((ac) =>
                          ac.asset_id === assetId
                        );

                        if (assetConsumption) {
                          chartData[t.energy_type?.id].asset_consumption += assetConsumption.energy_consumption;
                        }
                      }
                    }
                  }
                })
              }
            });
          }

          return chartData;
        }),
        shareReplay(1)
      );
    }
    return this.siteAssetConsumptions[key];
  }

  private initiativeFeasibilityData: ObservableMap<KPI.InitiativeFeasibilityData> = {};
  public getInitiativeFeasibilityData(
    buildings: number[],
    scenario: number
  ): Observable<KPI.InitiativeFeasibilityData> {
    const queryParams = { buildings: buildings.join(','), scenario };
    const key = this.getKey(arguments);
    if (!this.initiativeFeasibilityData[key]) {
      this.initiativeFeasibilityData[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => combineLatest([
          this.api.get_initiative_confidence_on_feasibility(queryParams),
          this.getCarbonIntensityPathway({ type_of_use: HARD_CODED_TYPE_OF_USE, target: '1.5' }),
          this.getCarbonIntensityPathway({ type_of_use: HARD_CODED_TYPE_OF_USE, target: '2.0' }),
        ])
        ),
        map(([initiativeFeasibilityData, sbti_15, sbti_2]) => {
          const sbti_15_object = sbti_15.find((pathway) => pathway.year === 2030);
          const sbti_2_object = sbti_2.find((pathway) => pathway.year === 2030);
          return {
            ...initiativeFeasibilityData,
            sbti_objectives: {
              sbti_15: sbti_15_object?.ghg_intensity || null,
              sbti_2: sbti_2_object?.ghg_intensity || null,
            }
          }
        }),
        shareReplay(1)
      );
    }
    return this.initiativeFeasibilityData[key];
  }

  private lifetimeVsTheorical: ObservableMap<KPI.LifetimeVsTheorical> = {};
  public getLifetimeVsTheorical(options: any): Observable<KPI.LifetimeVsTheorical> {
    const key = this.getKey(arguments);
    if (!this.lifetimeVsTheorical[key]) {
      this.lifetimeVsTheorical[key] = this.api.get_lifetime_vs_theorical(options).pipe(shareReplay(1));
    }
    return this.lifetimeVsTheorical[key];
  }


  // Use the following function to get the get the budget reporting for a single project

  // private budgetReporting: ObservableMap<KPI.BudgetReporting[]> = {};
  // public getBudgetReporting(buildingIds: any): Observable<KPI.BudgetReporting[]> {
  //   const key = this.getKey(arguments);
  //   if (!this.budgetReporting[key]) {
  //     this.budgetReporting[key] = this.api.get_budget_reporting(buildingIds).pipe(shareReplay(1));
  //   }
  //   return this.budgetReporting[key];
  // }

  private budgetReportingByInvestment: ObservableMap<KPI.BudgetReporting[]> = {};
  public getBudgetReportingByInvestment(investsIds: number[]): Observable<KPI.BudgetReporting[]> {
    const key = this.getKey(arguments);
    if (!this.budgetReportingByInvestment[key]) {
      this.budgetReportingByInvestment[key] = this._capexHasChanged$.pipe(
        switchMap(() => this.api.get_budget_reporting_by_investment(investsIds)),
      );
    }
    return this.budgetReportingByInvestment[key];
  }

  private globalBudgetHistory: ObservableMap<KPI.BudgetReporting[]> = {};
  public getGlobalBudgetHistory(year: number, buildings: string): Observable<KPI.BudgetReporting[]> {
    const key = this.getKey(arguments);
    if (!this.globalBudgetHistory[key]) {
      this.globalBudgetHistory[key] = this._capexHasChanged$.pipe(
        switchMap(() => this.api.get_global_budget_history(year, buildings)),
        shareReplay(1)
      );
    }
    return this.globalBudgetHistory[key];
  }

  private investmentsMonitoring: ObservableMap<KPI.InvestmentMonitoring> = {};
  public getInvestmentsMonitoring(params: {buildings: string}): Observable<KPI.InvestmentMonitoring> {
    const key = this.getKey(arguments);
    if (!this.investmentsMonitoring[key]) {
      this.investmentsMonitoring[key] = this.api.get_investments_monitoring(params).pipe(shareReplay(1))
    }
    return this.investmentsMonitoring[key];
  }

  private investmentTotalByStatus: ObservableMap<KPI.InvestmentTotalByStatus> = {};
  public getInvestmentTotalByStatus(params?: any): Observable<KPI.InvestmentTotalByStatus> {
    const key = this.getKey(arguments);
    if (!this.investmentTotalByStatus[key]) {
      this.investmentTotalByStatus[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.investments_investment_total_by_status(params)),
        shareReplay(1)
      );
    }
    return this.investmentTotalByStatus[key];
  }

  private globalInvestments: ObservableMap<KPI.GlobalInvestments> = {};
  public getGlobalInvestments(params?: any): Observable<KPI.GlobalInvestments> {
    const key = this.getKey(arguments);
    if (!this.globalInvestments[key]) {
      this.globalInvestments[key] = combineLatest([
        this._energyHasChanged$,
        this._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.investments_global_investments(params)),
        shareReplay(1)
      );
    }
    return this.globalInvestments[key];
  }

  private getKey(args: IArguments): string {
    return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
  }
}
